import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import {
  Calendar, Clock, Users, Sparkles, ChevronLeft, ChevronRight,
  Plus, X, Pencil, Trash2, Check, CheckCircle2, XCircle,
  Search, ToggleLeft, ToggleRight, RefreshCw, BedDouble,
  Activity, TrendingUp, Loader2, Star, Zap, CreditCard, Banknote, Hotel,
  ListChecks, DoorOpen, User, Package, ShoppingBag, Gift, Receipt,
  Smartphone, UserCheck, ClipboardList,
} from "lucide-react";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";
import "./SpaDashboard.css";
import SpaWaitlist from "./SpaWaitlist";
import SpaRooms    from "./SpaRooms";
import SpaGuests   from "./SpaGuests";
import SpaPackages from "./SpaPackages";
import SpaRetail   from "./SpaRetail";
import SpaVouchers from "./SpaVouchers";
import SpaSettlement from "./SpaSettlement";

// ─── Types ────────────────────────────────────────────────
interface SpaService {
  id: number;
  name: string;
  category: string;
  duration: number;
  price: number;
  description?: string;
  available: boolean;
  max_parallel: number;
  requires_staff: boolean;
}

interface SpaStaff {
  id: number;
  name: string;
  role: string;
  work_start: string;
  work_end: string;
  days_off: number[];
  service_ids: number[];
  available: boolean;
}

interface Appointment {
  id: number;
  guest_name: string;
  room_number?: string;
  phone?: string;
  service_id?: number;
  service_name?: string;
  staff_id?: number;
  staff_name?: string;
  date: string;
  time_slot: string;
  duration: number;
  price: number;
  status: string;
  notes?: string;
  payment_type?: string;
  booking_id?: string;
  payment_status?: string;
  created_at: string;
}

interface StaffSlots {
  staff_id: number | null;
  staff_name: string | null;
  role: string | null;
  available_slots: string[];
  max_parallel?: number;
  no_staff?: boolean;
}

interface BookingLookup {
  found: boolean;
  booking_id?: string;
  room_id?: string;
  guest_name?: string;
  phone?: string;
  checkin?: string;
  checkout?: string;
  services_price?: number;
  total_price?: number;
  payment_status?: string;
}

interface ActiveBooking {
  booking_id: string;
  room_number: string;
  guest_name: string | null;
  phone: string | null;
  checkin: string | null;
  checkout: string | null;
  services_price: number;
  total_price: number;
  payment_status: string | null;
}

interface Stats {
  total: number;
  confirmed: number;
  in_progress: number;
  completed: number;
  cancelled: number;
  revenue: number;
}

interface GuestSuggestion {
  booking_id: string;
  guest_name: string;
  phone: string | null;
  room_number: string | null;
  checkin: string | null;
  checkout: string | null;
}

interface AnalyticsData {
  period_days: number;
  period_revenue: number;
  prev_revenue: number;
  revenue_change_pct: number | null;
  period_bookings: number;
  prev_bookings: number;
  bookings_change_pct: number | null;
  alltime_revenue: number;
  alltime_bookings: number;
  by_day: { date: string; revenue: number; bookings: number }[];
  top_services: { name: string; bookings: number; revenue: number }[];
  staff_perf: { name: string; bookings: number; revenue: number }[];
  payment_split: { room_charge: number; direct_payment: number };
  status_counts: Record<string, number>;
}

interface DayAvail {
  date: string;
  status: "off" | "available" | "busy" | "full";
  booked_mins: number;
  work_mins: number;
  utilization: number;
  appointments: number;
}

interface WeekStaffAvail {
  staff_id: number;
  staff_name: string;
  role: string;
  days: DayAvail[];
}

// ─── Constants ────────────────────────────────────────────
const API = (import.meta.env.VITE_API_URL ?? "http://localhost:5050") + "/api/spa";

const STATUS_META: Record<string, { label: string; color: string; bg: string }> = {
  confirmed:   { label: "Confirmed",   color: "#6366f1", bg: "rgba(99,102,241,0.12)" },
  arrived:     { label: "Arrived",     color: "#fbbf24", bg: "rgba(251,191,36,0.12)" },
  in_progress: { label: "In Progress", color: "#34d399", bg: "rgba(52,211,153,0.12)" },
  completed:   { label: "Completed",   color: "#10b981", bg: "rgba(16,185,129,0.12)" },
  cancelled:   { label: "Cancelled",   color: "#f87171", bg: "rgba(239,68,68,0.1)"   },
  no_show:     { label: "No Show",     color: "rgba(242,235,217,0.3)", bg: "rgba(148,163,184,0.1)" },
};

const CATEGORIES = ["Massage", "Facial", "Body Treatment", "Nail Care", "Hair", "Wellness", "Other"];
const DAY_NAMES  = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const GRID_START = 8 * 60;
const GRID_END   = 20 * 60;
const ROW_H      = 48;

function toMins(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

function fmt12(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12  = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function catClass(category: string): string {
  return category.toLowerCase().replace(/\s+/g, "-");
}

function initials(name: string): string {
  return name.split(" ").map(p => p[0]).slice(0, 2).join("").toUpperCase();
}

function getWeekStart(dateStr: string): string {
  const d = new Date(dateStr + "T12:00:00");
  const day = d.getDay(); // 0=Sun
  const mon = new Date(d);
  mon.setDate(d.getDate() - ((day + 6) % 7)); // shift to Monday
  return `${mon.getFullYear()}-${String(mon.getMonth() + 1).padStart(2, "0")}-${String(mon.getDate()).padStart(2, "0")}`;
}

function shiftDate(dateStr: string, delta: number): string {
  const d = new Date(dateStr + "T12:00:00");
  d.setDate(d.getDate() + delta);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmtDate(dateStr: string): string {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

function fmtShortDate(dateStr: string): string {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ─── Component ────────────────────────────────────────────
export default function SpaDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "overview";

  // ── Data ─────────────────────────────────────────────────
  const [stats,        setStats]        = useState<Stats | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [calAppts,     setCalAppts]     = useState<Appointment[]>([]);
  const [services,     setServices]     = useState<SpaService[]>([]);
  const [staff,        setStaff]        = useState<SpaStaff[]>([]);

  // ── Calendar tab ─────────────────────────────────────────
  const [calDate,      setCalDate]      = useState(todayStr());
  const [calMonth,     setCalMonth]     = useState(new Date().getMonth() + 1); // 1-based
  const [calYear,      setCalYear]      = useState(new Date().getFullYear());
  const [monthSummary, setMonthSummary] = useState<Record<string, { count: number; revenue: number }>>({});
  const [loadingMonth, setLoadingMonth] = useState(false);

  // ── Availability tab ─────────────────────────────────────
  const [weekStart,   setWeekStart]   = useState(() => getWeekStart(todayStr()));
  const [weekAvail,   setWeekAvail]   = useState<WeekStaffAvail[]>([]);
  const [loadingWeek, setLoadingWeek] = useState(false);
  const [drillCell,      setDrillCell]      = useState<{ staff: WeekStaffAvail; day: DayAvail } | null>(null);
  const [drillSlots,     setDrillSlots]     = useState<StaffSlots[]>([]);
  const [drillLoading,   setDrillLoading]   = useState(false);
  const [drillServiceId, setDrillServiceId] = useState<number | null>(null);

  // ── Appointments tab ─────────────────────────────────────
  const [apptDate,   setApptDate]   = useState(todayStr());
  const [apptStatus, setApptStatus] = useState("all");
  const [apptSearch, setApptSearch] = useState("");

  // ── Services tab ─────────────────────────────────────────
  const [svcSearch, setSvcSearch] = useState("");
  const [editSvc,   setEditSvc]   = useState<Partial<SpaService> | null>(null);
  const [svcModal,  setSvcModal]  = useState(false);

  // ── Staff tab ────────────────────────────────────────────
  const [editStaff,  setEditStaff]  = useState<Partial<SpaStaff> | null>(null);
  const [staffModal, setStaffModal] = useState(false);

  // ── Role ─────────────────────────────────────────────────
  const userRole = (() => {
    try { return (jwtDecode(localStorage.getItem("ai2_token") || "") as any).role || "spa"; }
    catch { return "spa"; }
  })();
  const isSpaManager = userRole === "spa_manager" || userRole === "manager" || userRole === "admin";

  // ── Booking wizard ───────────────────────────────────────
  const [showBooking,    setShowBooking]    = useState(false);
  const [bookingStep,    setBookingStep]    = useState<1 | 2 | 3>(1);
  const [bookingData,    setBookingData]    = useState<Record<string, any>>({});
  const [availableSlots, setAvailableSlots] = useState<StaffSlots[]>([]);
  const [_slotsCapacity, setSlotsCapacity]  = useState<Record<string, number>>({});
  const [_slotsMaxPar,   setSlotsMaxPar]    = useState(1);
  const [slotStaffTab,   setSlotStaffTab]   = useState(0);
  const [slotsLoading,   setSlotsLoading]   = useState(false);
  // Payment
  const [paymentType,    setPaymentType]    = useState<"room_charge" | "direct_payment">("direct_payment");
  const [paymentMethod,  setPaymentMethod]  = useState<"cash" | "card" | "instapay">("cash");
  const [cardNumber,     setCardNumber]     = useState("");
  const [transactionRef, setTransactionRef] = useState("");
  const [roomLookup,     setRoomLookup]     = useState<BookingLookup | null>(null);
  const [lookingUpRoom,  setLookingUpRoom]  = useState(false);
  const roomLookupTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Calendar appointment detail ──────────────────────────
  const [apptDetail, setApptDetail] = useState<Appointment | null>(null);
  // ── Intake form (linked to appointment detail) ───────────
  const [intakeData,    setIntakeData]    = useState<{ allergies?: string; medical_conditions?: string; pressure_preference?: string; focus_areas?: string; additional_notes?: string } | null>(null);
  const [showIntake,    setShowIntake]    = useState(false);
  const [intakeForm,    setIntakeForm]    = useState({ allergies: "", medical_conditions: "", pressure_preference: "medium", focus_areas: "", additional_notes: "" });
  const [savingIntake,  setSavingIntake]  = useState(false);

  // ── Active bookings (for room_charge dropdown) ───────────
  const [activeBookings,        setActiveBookings]        = useState<ActiveBooking[]>([]);
  const [loadingActiveBookings, setLoadingActiveBookings] = useState(false);
  const [roomSearch,            setRoomSearch]            = useState("");

  // ── Analytics tab ────────────────────────────────────────
  const [analytics,        setAnalytics]        = useState<AnalyticsData | null>(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  // ── Guest autocomplete ───────────────────────────────────
  const [guestSuggestions,    setGuestSuggestions]    = useState<GuestSuggestion[]>([]);
  const [showGuestDropdown,   setShowGuestDropdown]   = useState(false);
  const [searchingGuests,     setSearchingGuests]     = useState(false);
  const guestSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Loading ──────────────────────────────────────────────
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingAppts, setLoadingAppts] = useState(false);
  const [loadingSvc,   setLoadingSvc]   = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [saveError,    setSaveError]    = useState("");

  // ─── Fetchers ────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const { data } = await axios.get(`${API}/stats`);
      setStats(data);
    } catch { /**/ } finally { setLoadingStats(false); }
  }, []);

  const fetchTodayAppts = useCallback(async () => {
    setLoadingAppts(true);
    try {
      const { data } = await axios.get(`${API}/appointments?date=${todayStr()}`);
      setAppointments(data);
    } catch { /**/ } finally { setLoadingAppts(false); }
  }, []);

  const fetchCalAppts = useCallback(async (date: string) => {
    setLoadingAppts(true);
    try {
      const { data } = await axios.get(`${API}/appointments?date=${date}`);
      setCalAppts(data);
    } catch { /**/ } finally { setLoadingAppts(false); }
  }, []);

  const fetchAppts = useCallback(async (date: string, status: string) => {
    setLoadingAppts(true);
    try {
      const params = new URLSearchParams({ date });
      if (status !== "all") params.set("status", status);
      const { data } = await axios.get(`${API}/appointments?${params}`);
      setAppointments(data);
    } catch { /**/ } finally { setLoadingAppts(false); }
  }, []);

  const fetchServices = useCallback(async () => {
    setLoadingSvc(true);
    try {
      const { data } = await axios.get(`${API}/services`);
      setServices(data);
    } catch { /**/ } finally { setLoadingSvc(false); }
  }, []);

  const fetchStaff = useCallback(async () => {
    setLoadingStaff(true);
    try {
      const { data } = await axios.get(`${API}/staff`);
      setStaff(data);
    } catch { /**/ } finally { setLoadingStaff(false); }
  }, []);

  const fetchMonthSummary = useCallback(async (year: number, month: number) => {
    setLoadingMonth(true);
    try {
      const { data } = await axios.get(`${API}/month-summary?year=${year}&month=${month}`);
      setMonthSummary(data);
    } catch { /**/ } finally { setLoadingMonth(false); }
  }, []);

  const fetchActiveBookings = useCallback(async () => {
    setLoadingActiveBookings(true);
    try {
      const { data } = await axios.get(`${API}/active-bookings`);
      setActiveBookings(data);
    } catch { /**/ } finally { setLoadingActiveBookings(false); }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    setLoadingAnalytics(true);
    try {
      const { data } = await axios.get(`${API}/analytics`);
      setAnalytics(data);
    } catch { /**/ } finally { setLoadingAnalytics(false); }
  }, []);

  const searchGuests = async (q: string) => {
    if (!q.trim()) { setGuestSuggestions([]); return; }
    setSearchingGuests(true);
    try {
      const { data } = await axios.get(`${API}/search-guests?q=${encodeURIComponent(q)}`);
      setGuestSuggestions(data);
      setShowGuestDropdown(data.length > 0);
    } catch { setGuestSuggestions([]); } finally { setSearchingGuests(false); }
  };

  const fetchWeekAvail = useCallback(async (ws: string) => {
    setLoadingWeek(true);
    try {
      const { data } = await axios.get(`${API}/week-availability?week_start=${ws}`);
      setWeekAvail(data);
    } catch { /**/ } finally { setLoadingWeek(false); }
  }, []);

  const fetchDrillSlots = async (serviceId: number, date: string) => {
    setDrillLoading(true);
    try {
      const { data } = await axios.get(`${API}/slots?date=${date}&service_id=${serviceId}`);
      setDrillSlots(data.staff ?? data); // handle both formats
    } catch { setDrillSlots([]); } finally { setDrillLoading(false); }
  };

  // ─── Tab effects ─────────────────────────────────────────
  useEffect(() => {
    if (tab === "overview") {
      fetchStats();
      fetchTodayAppts();
    } else if (tab === "calendar") {
      fetchCalAppts(calDate);
      fetchStaff();
      fetchMonthSummary(calYear, calMonth);
    } else if (tab === "appointments") {
      fetchAppts(apptDate, apptStatus);
    } else if (tab === "services") {
      fetchServices();
    } else if (tab === "staff") {
      fetchStaff();
      fetchServices();
    } else if (tab === "waitlist" || tab === "packages") {
      if (services.length === 0) fetchServices();
    } else if (tab === "availability") {
      fetchWeekAvail(weekStart);
      fetchStaff();
    } else if (tab === "analytics") {
      fetchAnalytics();
    }
  }, [tab]);

  useEffect(() => {
    if (tab === "calendar") fetchCalAppts(calDate);
  }, [calDate]);

  useEffect(() => {
    if (tab === "calendar") fetchMonthSummary(calYear, calMonth);
  }, [calYear, calMonth]);

  useEffect(() => {
    if (tab === "appointments") fetchAppts(apptDate, apptStatus);
  }, [apptDate, apptStatus]);

  useEffect(() => {
    if (tab === "availability") fetchWeekAvail(weekStart);
  }, [weekStart]);

  // Auto-jump staff tab when slots load and staff is pre-filled
  useEffect(() => {
    if (!bookingData.staff_id || availableSlots.length === 0) return;
    const idx = availableSlots.findIndex(ss => ss.staff_id === bookingData.staff_id);
    if (idx >= 0) setSlotStaffTab(idx);
  }, [availableSlots]);

  // sync mini-cal month when calDate changes month
  useEffect(() => {
    const d = new Date(calDate + "T12:00:00");
    setCalYear(d.getFullYear());
    setCalMonth(d.getMonth() + 1);
  }, [calDate]);

  // ─── Tab helpers ─────────────────────────────────────────
  const setTab = (t: string) => setSearchParams({ tab: t });

  // ─── Intake form helpers ──────────────────────────────────
  const fetchIntake = async (apptId: number) => {
    try {
      const { data } = await axios.get(`${API}/intake/${apptId}`);
      if (data) { setIntakeData(data); setIntakeForm({ allergies: data.allergies || "", medical_conditions: data.medical_conditions || "", pressure_preference: data.pressure_preference || "medium", focus_areas: data.focus_areas || "", additional_notes: data.additional_notes || "" }); }
      else { setIntakeData(null); setIntakeForm({ allergies: "", medical_conditions: "", pressure_preference: "medium", focus_areas: "", additional_notes: "" }); }
    } catch { /**/ }
  };

  const saveIntake = async () => {
    if (!apptDetail) return;
    setSavingIntake(true);
    try {
      await axios.post(`${API}/intake`, { appointment_id: apptDetail.id, guest_name: apptDetail.guest_name, ...intakeForm });
      setIntakeData({ ...intakeForm }); setShowIntake(false);
    } catch { /**/ } finally { setSavingIntake(false); }
  };

  // ─── Appointment status update ────────────────────────────
  const updateStatus = async (id: number, status: string) => {
    try {
      await axios.put(`${API}/appointments/${id}`, { status });
      if (tab === "overview") { fetchStats(); fetchTodayAppts(); }
      if (tab === "calendar") fetchCalAppts(calDate);
      if (tab === "appointments") fetchAppts(apptDate, apptStatus);
    } catch { /**/ }
  };

  const deleteAppt = async (id: number) => {
    if (!confirm("Delete this appointment?")) return;
    try {
      await axios.delete(`${API}/appointments/${id}`);
      if (tab === "overview") { fetchStats(); fetchTodayAppts(); }
      if (tab === "appointments") fetchAppts(apptDate, apptStatus);
    } catch { /**/ }
  };

  // ─── Service CRUD ─────────────────────────────────────────
  const openNewSvc  = () => { setEditSvc({ name: "", category: "Massage", duration: 60, price: 0, description: "", available: true, max_parallel: 1, requires_staff: true }); setSvcModal(true); };
  const openEditSvc = (svc: SpaService) => { setEditSvc({ ...svc }); setSvcModal(true); };

  const saveSvc = async () => {
    if (!editSvc) return;
    setSaving(true);
    try {
      if (editSvc.id) await axios.put(`${API}/services/${editSvc.id}`, editSvc);
      else             await axios.post(`${API}/services`, editSvc);
      setSvcModal(false); setEditSvc(null); fetchServices();
    } catch { /**/ } finally { setSaving(false); }
  };

  const deleteSvc = async (id: number) => {
    if (!confirm("Delete this service?")) return;
    try { await axios.delete(`${API}/services/${id}`); fetchServices(); } catch { /**/ }
  };

  // ─── Staff CRUD ───────────────────────────────────────────
  const openNewStaff  = () => { setEditStaff({ name: "", role: "", work_start: "09:00", work_end: "18:00", days_off: [], service_ids: [], available: true }); setStaffModal(true); };
  const openEditStaff = (s: SpaStaff) => { setEditStaff({ ...s }); setStaffModal(true); };

  const saveStaff = async () => {
    if (!editStaff) return;
    setSaving(true);
    try {
      if (editStaff.id) await axios.put(`${API}/staff/${editStaff.id}`, editStaff);
      else               await axios.post(`${API}/staff`, editStaff);
      setStaffModal(false); setEditStaff(null); fetchStaff();
    } catch { /**/ } finally { setSaving(false); }
  };

  const deleteStaff = async (id: number) => {
    if (!confirm("Delete this staff member?")) return;
    try { await axios.delete(`${API}/staff/${id}`); fetchStaff(); } catch { /**/ }
  };

  // ─── Booking Wizard ───────────────────────────────────────
  const openBooking = (
    prefillDate?: string,
    prefillStaffId?: number,
    prefillStaffName?: string,
    prefillTime?: string,
    prefillSvc?: { id: number; name: string; duration: number; price: number },
    startStep?: 1 | 2 | 3,
  ) => {
    setBookingData({
      date: prefillDate || todayStr(),
      staff_id:   prefillStaffId,
      staff_name: prefillStaffName,
      time_slot:  prefillTime,
      ...(prefillSvc ? { service_id: prefillSvc.id, service_name: prefillSvc.name, duration: prefillSvc.duration, price: prefillSvc.price } : {}),
    });
    setBookingStep(startStep || 1);
    setAvailableSlots([]);
    setSlotsCapacity({});
    setSlotsMaxPar(1);
    setSlotStaffTab(0);
    setPaymentType("direct_payment");
    setPaymentMethod("cash");
    setCardNumber("");
    setTransactionRef("");
    setRoomLookup(null);
    setRoomSearch("");
    setShowBooking(true);
    if (services.length === 0) fetchServices();
    // if jumping to step 2, pre-fetch slots
    if ((startStep === 2) && prefillSvc) {
      fetchSlots(prefillSvc.id, prefillDate || todayStr());
    }
  };

  const fetchSlots = async (serviceId: number, date: string) => {
    setSlotsLoading(true);
    try {
      const { data } = await axios.get(`${API}/slots?date=${date}&service_id=${serviceId}`);
      const staffSlots: StaffSlots[] = data.staff ?? data;
      setAvailableSlots(staffSlots);
      setSlotsCapacity(data.capacity_used ?? {});
      setSlotsMaxPar(data.max_parallel ?? 1);
      setSlotStaffTab(0);
    } catch { setAvailableSlots([]); } finally { setSlotsLoading(false); }
  };

  const lookupRoomBooking = async (room: string) => {
    if (!room) { setRoomLookup(null); return; }
    setLookingUpRoom(true);
    try {
      const { data } = await axios.get(`${API}/lookup-booking?room=${encodeURIComponent(room)}`);
      setRoomLookup(data);
      if (data.found && data.guest_name) {
        setBookingData(d => ({ ...d, guest_name: data.guest_name, phone: data.phone || d.phone }));
      }
    } catch { setRoomLookup(null); } finally { setLookingUpRoom(false); }
  };

  const selectService = (svc: SpaService) => {
    setBookingData(d => ({ ...d, service_id: svc.id, service_name: svc.name, duration: svc.duration, price: svc.price }));
  };

  const goToStep2 = () => {
    if (!bookingData.service_id) return;
    setBookingStep(2);
    fetchSlots(bookingData.service_id, bookingData.date || todayStr());
  };

  const selectSlot = (staffSlot: StaffSlots, slot: string) => {
    setBookingData(d => ({ ...d, staff_id: staffSlot.staff_id, staff_name: staffSlot.staff_name, time_slot: slot }));
  };

  const confirmBooking = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/appointments`, {
        guest_name:     bookingData.guest_name,
        room_number:    bookingData.room_number,
        phone:          bookingData.phone,
        service_id:     bookingData.service_id,
        service_name:   bookingData.service_name,
        staff_id:       bookingData.staff_id,
        staff_name:     bookingData.staff_name,
        date:           bookingData.date,
        time_slot:      bookingData.time_slot,
        duration:       bookingData.duration,
        price:          bookingData.price,
        notes:          bookingData.notes,
        payment_type:    paymentType,
        booking_id:      paymentType === "room_charge" ? roomLookup?.booking_id : null,
        payment_method:  paymentType === "direct_payment" ? paymentMethod : null,
        card_number:     paymentMethod === "card"    ? cardNumber.trim()     || null : null,
        transaction_ref: paymentMethod === "instapay" ? transactionRef.trim() || null : null,
      });
      setShowBooking(false);
      setBookingData({});
      if (tab === "overview")      { fetchStats(); fetchTodayAppts(); }
      if (tab === "calendar")      fetchCalAppts(calDate);
      if (tab === "appointments")  fetchAppts(apptDate, apptStatus);
      if (tab === "availability")  { fetchWeekAvail(weekStart); fetchMonthSummary(calYear, calMonth); }
    } catch (e: any) {
      setSaveError(e?.response?.data?.error ?? e?.message ?? "Failed to save appointment");
    } finally { setSaving(false); }
  };

  // ─── Filtered list ────────────────────────────────────────
  const filteredAppts = appointments.filter(a => {
    if (!apptSearch) return true;
    const q = apptSearch.toLowerCase();
    return (
      a.guest_name?.toLowerCase().includes(q) ||
      a.service_name?.toLowerCase().includes(q) ||
      a.staff_name?.toLowerCase().includes(q) ||
      a.room_number?.toLowerCase().includes(q)
    );
  });

  // ─── Calendar grid ────────────────────────────────────────
  const timeRows: number[] = [];
  for (let m = GRID_START; m < GRID_END; m += 30) timeRows.push(m);

  // ─── Monthly mini-calendar ────────────────────────────────
  function buildMiniCal(year: number, month: number) {
    // month is 1-based
    const firstDay = new Date(year, month - 1, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(year, month, 0).getDate();
    const cells: (number | null)[] = Array(firstDay).fill(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }

  const miniCalCells = buildMiniCal(calYear, calMonth);
  const today = todayStr();

  // ─── Availability helpers ─────────────────────────────────
  const weekDates: string[] = [];
  for (let i = 0; i < 7; i++) weekDates.push(shiftDate(weekStart, i));

  function availColor(status: DayAvail["status"]): string {
    if (status === "off")       return "rgba(30,41,59,0.6)";
    if (status === "full")      return "rgba(239,68,68,0.15)";
    if (status === "busy")      return "rgba(251,191,36,0.13)";
    return "rgba(52,211,153,0.1)";
  }

  function availTextColor(status: DayAvail["status"]): string {
    if (status === "off")  return "#334155";
    if (status === "full") return "#f87171";
    if (status === "busy") return "#fbbf24";
    return "#34d399";
  }

  // ─── Render ───────────────────────────────────────────────
  return (
    <DashboardLayout>
      <div className="spa-page">

        {/* Header */}
        <div className="spa-header">
          <div>
            <h1 className="spa-title">Spa & Wellness</h1>
            <p className="spa-subtitle">Manage appointments, services, and staff</p>
          </div>
          <div className="spa-header-right">
            <button className="spa-btn-ghost" onClick={() => {
              if (tab === "overview")     { fetchStats(); fetchTodayAppts(); }
              if (tab === "calendar")     { fetchCalAppts(calDate); fetchMonthSummary(calYear, calMonth); }
              if (tab === "appointments") fetchAppts(apptDate, apptStatus);
              if (tab === "availability") fetchWeekAvail(weekStart);
            }}>
              <RefreshCw size={14} /> Refresh
            </button>
            <button className="spa-btn-primary" onClick={() => openBooking()}>
              <Plus size={14} /> New Booking
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="spa-tabs">
          {[
            { key: "overview",      label: "Overview",      icon: <Activity size={14} />, always: true },
            { key: "calendar",      label: "Calendar",      icon: <Calendar size={14} />, always: true },
            { key: "availability",  label: "Availability",  icon: <Zap size={14} />,      always: true },
            { key: "appointments",  label: "Appointments",  icon: <Clock size={14} />,    always: true },
            { key: "analytics",     label: "Analytics",     icon: <TrendingUp size={14} />,  always: false },
            { key: "services",      label: "Services",      icon: <Sparkles size={14} />,   always: true },
            { key: "staff",         label: "Staff",         icon: <Users size={14} />,       always: true },
            { key: "waitlist",      label: "Waitlist",      icon: <ListChecks size={14} />,  always: true },
            { key: "rooms",         label: "Rooms",         icon: <DoorOpen size={14} />,    always: true },
            { key: "guests",        label: "Guests",        icon: <User size={14} />,         always: true },
            { key: "packages",      label: "Packages",      icon: <Package size={14} />,      always: false },
            { key: "retail",        label: "Retail",        icon: <ShoppingBag size={14} />,  always: false },
            { key: "vouchers",      label: "Vouchers",      icon: <Gift size={14} />,         always: false },
            { key: "settlement",    label: "Settlement",    icon: <Receipt size={14} />,      always: false },
          ].filter(t => t.always || isSpaManager).map(t => (
            <button
              key={t.key}
              className={`spa-tab ${tab === t.key ? "active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* ══ OVERVIEW TAB ═══════════════════════════════════ */}
        {tab === "overview" && (
          <>
            <div className="spa-kpis">
              <div className="spa-kpi-card">
                <div className="spa-kpi-icon" style={{ background: "rgba(99,102,241,0.15)" }}>
                  <Calendar size={22} color="#818cf8" />
                </div>
                <div>
                  <div className="spa-kpi-value">{loadingStats ? "—" : stats?.total ?? 0}</div>
                  <div className="spa-kpi-label">Today's Bookings</div>
                </div>
              </div>
              <div className="spa-kpi-card">
                <div className="spa-kpi-icon" style={{ background: "rgba(251,191,36,0.12)" }}>
                  <Activity size={22} color="#fbbf24" />
                </div>
                <div>
                  <div className="spa-kpi-value">{loadingStats ? "—" : stats?.in_progress ?? 0}</div>
                  <div className="spa-kpi-label">In Progress</div>
                </div>
              </div>
              <div className="spa-kpi-card">
                <div className="spa-kpi-icon" style={{ background: "rgba(52,211,153,0.12)" }}>
                  <CheckCircle2 size={22} color="#34d399" />
                </div>
                <div>
                  <div className="spa-kpi-value">{loadingStats ? "—" : stats?.completed ?? 0}</div>
                  <div className="spa-kpi-label">Completed</div>
                </div>
              </div>
              {isSpaManager && (
                <div className="spa-kpi-card">
                  <div className="spa-kpi-icon" style={{ background: "rgba(52,211,153,0.08)" }}>
                    <TrendingUp size={22} color="#34d399" />
                  </div>
                  <div>
                    <div className="spa-price-value" style={{ fontSize: 22 }}>
                      ${loadingStats ? "—" : (stats?.revenue ?? 0).toFixed(0)}
                    </div>
                    <div className="spa-kpi-label">Today's Revenue</div>
                  </div>
                </div>
              )}
            </div>

            <h2 className="spa-section-title">Today's Schedule</h2>
            <div className="spa-schedule-list">
              {loadingAppts ? (
                <div className="spa-loading"><Loader2 size={18} className="spa-spinner" /> Loading schedule…</div>
              ) : appointments.length === 0 ? (
                <div className="spa-no-appointments">No appointments today.</div>
              ) : (
                [...appointments].sort((a, b) => a.time_slot.localeCompare(b.time_slot)).map(appt => (
                  <div key={appt.id} className="spa-schedule-row">
                    <div className="spa-time-col">{fmt12(appt.time_slot)}</div>
                    <div className="spa-appt-info">
                      <div className="spa-appt-guest">{appt.guest_name}</div>
                      <div className="spa-appt-service">{appt.service_name} · {appt.duration} min</div>
                      {appt.staff_name && <div className="spa-appt-therapist">with {appt.staff_name}</div>}
                    </div>
                    <span className="spa-status-badge" style={{ color: STATUS_META[appt.status]?.color, background: STATUS_META[appt.status]?.bg }}>
                      {STATUS_META[appt.status]?.label ?? appt.status}
                    </span>
                    <div className="spa-row-actions">
                      {appt.status === "confirmed" && (
                        <button className="spa-action-btn warn" onClick={() => updateStatus(appt.id, "in_progress")}><Activity size={12} /> Start</button>
                      )}
                      {appt.status === "in_progress" && (
                        <button className="spa-action-btn success" onClick={() => updateStatus(appt.id, "completed")}><Check size={12} /> Done</button>
                      )}
                      {(appt.status === "confirmed" || appt.status === "in_progress") && (
                        <button className="spa-action-btn danger" onClick={() => updateStatus(appt.id, "cancelled")}><XCircle size={12} /> Cancel</button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* ══ CALENDAR TAB ═══════════════════════════════════ */}
        {tab === "calendar" && (
          <div className="spa-cal-split">

            {/* Left: Monthly mini-calendar */}
            <div className="spa-mini-cal-panel">
              <div className="spa-mini-cal-header">
                <button className="spa-cal-nav-btn" onClick={() => {
                  if (calMonth === 1) { setCalMonth(12); setCalYear(y => y - 1); }
                  else setCalMonth(m => m - 1);
                }}>
                  <ChevronLeft size={14} />
                </button>
                <span className="spa-mini-cal-title">{MONTH_NAMES[calMonth - 1]} {calYear}</span>
                <button className="spa-cal-nav-btn" onClick={() => {
                  if (calMonth === 12) { setCalMonth(1); setCalYear(y => y + 1); }
                  else setCalMonth(m => m + 1);
                }}>
                  <ChevronRight size={14} />
                </button>
              </div>

              {/* Day-of-week header */}
              <div className="spa-mini-cal-dow">
                {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => (
                  <div key={d} className="spa-mini-cal-dow-cell">{d}</div>
                ))}
              </div>

              {/* Calendar cells */}
              <div className="spa-mini-cal-grid">
                {loadingMonth && <div className="spa-mini-cal-loading"><Loader2 size={14} className="spa-spinner" /></div>}
                {miniCalCells.map((day, idx) => {
                  if (!day) return <div key={idx} className="spa-mini-cal-cell empty" />;
                  const dateStr = `${calYear}-${String(calMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  const summary = monthSummary[dateStr];
                  const isToday    = dateStr === today;
                  const isSelected = dateStr === calDate;
                  return (
                    <div
                      key={idx}
                      className={`spa-mini-cal-cell ${isToday ? "today" : ""} ${isSelected ? "selected" : ""} ${summary?.count ? "has-appts" : ""}`}
                      onClick={() => setCalDate(dateStr)}
                    >
                      <span className="spa-mini-cal-day-num">{day}</span>
                      {summary?.count ? (
                        <span className="spa-mini-cal-badge">{summary.count}</span>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              {/* Month summary legend */}
              <div className="spa-mini-cal-legend">
                <div className="spa-mini-cal-legend-row">
                  <span className="spa-mini-cal-legend-dot" style={{ background: "#818cf8" }} />
                  <span>Appointments this month: <strong>{Object.values(monthSummary).reduce((s, d) => s + d.count, 0)}</strong></span>
                </div>
                {isSpaManager && (
                  <div className="spa-mini-cal-legend-row">
                    <span className="spa-mini-cal-legend-dot" style={{ background: "#34d399" }} />
                    <span>Revenue: <strong>${Object.values(monthSummary).reduce((s, d) => s + d.revenue, 0).toFixed(0)}</strong></span>
                  </div>
                )}
              </div>

              <button
                className="spa-btn-ghost spa-mini-today-btn"
                onClick={() => setCalDate(today)}
              >
                Jump to Today
              </button>
            </div>

            {/* Right: Day grid */}
            <div className="spa-cal-day-panel">
              <div className="spa-cal-header">
                <button className="spa-cal-nav-btn" onClick={() => setCalDate(d => shiftDate(d, -1))}>
                  <ChevronLeft size={16} />
                </button>
                <div>
                  <span className="spa-cal-date">{fmtDate(calDate)}</span>
                  {calDate === today && <span className="spa-today-chip">Today</span>}
                </div>
                <button className="spa-cal-nav-btn" onClick={() => setCalDate(d => shiftDate(d, 1))}>
                  <ChevronRight size={16} />
                </button>
              </div>

              {/* Appointment summary for this day */}
              {calAppts.length > 0 && (
                <div className="spa-day-summary-bar">
                  {Object.entries(STATUS_META).map(([k, v]) => {
                    const cnt = calAppts.filter(a => a.status === k).length;
                    if (!cnt) return null;
                    return (
                      <span key={k} className="spa-day-summary-chip" style={{ color: v.color, background: v.bg }}>
                        {cnt} {v.label}
                      </span>
                    );
                  })}
                  {isSpaManager && (
                    <span className="spa-day-summary-rev">
                      Revenue: ${calAppts.filter(a => a.status === "completed").reduce((s, a) => s + (a.price || 0), 0).toFixed(0)}
                    </span>
                  )}
                </div>
              )}

              {(() => {
                const selfServiceAppts = calAppts.filter(a => !a.staff_id);
                const hasSelfService   = selfServiceAppts.length > 0;
                const colCount         = staff.length + (hasSelfService ? 1 : 0);

                if (colCount === 0) return (
                  <div className="spa-empty">
                    <div className="spa-empty-icon"><Users size={36} /></div>
                    <p>No appointments today. Add staff or book a staff-free service to see the calendar.</p>
                  </div>
                );

                const renderApptBlocks = (appts: Appointment[]) => appts.map(appt => {
                  const apptMins = toMins(appt.time_slot);
                  if (apptMins < GRID_START || apptMins >= GRID_END) return null;
                  const top    = ((apptMins - GRID_START) / 30) * ROW_H;
                  const height = Math.max((appt.duration / 30) * ROW_H - 2, 22);
                  const meta   = STATUS_META[appt.status] ?? STATUS_META.confirmed;
                  return (
                    <div
                      key={appt.id}
                      className="spa-appt-block"
                      style={{ top, height, background: meta.bg, borderLeft: `3px solid ${meta.color}` }}
                      title={`${appt.guest_name} — ${appt.service_name} (${fmt12(appt.time_slot)})`}
                      onClick={e => { e.stopPropagation(); setApptDetail(appt); setShowIntake(false); fetchIntake(appt.id); }}
                    >
                      <div className="spa-appt-block-guest">{appt.guest_name}</div>
                      {height > 36 && <div className="spa-appt-block-service">{appt.service_name}</div>}
                      {height > 52 && <div className="spa-appt-block-time">{fmt12(appt.time_slot)}</div>}
                      {appt.payment_type === "room_charge" && <div className="spa-appt-block-badge room">Room</div>}
                    </div>
                  );
                });

                return (
                  <>
                    <div
                      className="spa-staff-cols-headers"
                      style={{ gridTemplateColumns: `60px repeat(${colCount}, 1fr)`, display: "grid" }}
                    >
                      <div style={{ borderRight: "1px solid rgba(255,255,255,0.06)" }} />
                      {hasSelfService && (
                        <div className="spa-staff-col-header-cell">
                          <div className="spa-staff-col-name" style={{ color: "#c9a84c" }}>Self-Service</div>
                          <div className="spa-staff-col-role">No therapist</div>
                        </div>
                      )}
                      {staff.map(s => (
                        <div key={s.id} className="spa-staff-col-header-cell">
                          <div className="spa-staff-col-name">{s.name}</div>
                          <div className="spa-staff-col-role">{s.role}</div>
                        </div>
                      ))}
                    </div>

                    <div className="spa-cal-grid-outer">
                      <div className="spa-time-grid-container">
                        <div className="spa-time-axis-col">
                          {timeRows.map(m => (
                            <div key={m} className="spa-time-label" style={{ top: ((m - GRID_START) / 30) * ROW_H + ROW_H / 2 }}>
                              {fmt12(`${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`)}
                            </div>
                          ))}
                        </div>

                        <div className="spa-staff-cols-area" style={{ gridTemplateColumns: `repeat(${colCount}, 1fr)` }}>
                          {/* Self-service column */}
                          {hasSelfService && (
                            <div
                              className="spa-staff-col-body spa-staff-col-clickable"
                              onClick={e => {
                                if ((e.target as HTMLElement).closest(".spa-appt-block")) return;
                                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                const scrollTop = (e.currentTarget as HTMLElement).parentElement?.parentElement?.scrollTop ?? 0;
                                const clickY = e.clientY - rect.top + scrollTop;
                                const slotMins = GRID_START + Math.floor(clickY / ROW_H) * 30;
                                if (slotMins >= GRID_END) return;
                                const h = Math.floor(slotMins / 60), mm = slotMins % 60;
                                openBooking(calDate, undefined, undefined, `${String(h).padStart(2,"0")}:${String(mm).padStart(2,"0")}`);
                              }}
                            >
                              {timeRows.map((m, i) => <div key={m} className="spa-grid-row-line" style={{ top: i * ROW_H }} />)}
                              {renderApptBlocks(selfServiceAppts)}
                            </div>
                          )}
                          {/* Staff columns */}
                          {staff.map(s => {
                            const colAppts = calAppts.filter(a => a.staff_id === s.id);
                            return (
                              <div
                                key={s.id}
                                className="spa-staff-col-body spa-staff-col-clickable"
                                onClick={e => {
                                  if ((e.target as HTMLElement).closest(".spa-appt-block")) return;
                                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                  const scrollTop = (e.currentTarget as HTMLElement).parentElement?.parentElement?.scrollTop ?? 0;
                                  const clickY = e.clientY - rect.top + scrollTop;
                                  const slotMins = GRID_START + Math.floor(clickY / ROW_H) * 30;
                                  if (slotMins >= GRID_END) return;
                                  const h = Math.floor(slotMins / 60), mm = slotMins % 60;
                                  openBooking(calDate, s.id, s.name, `${String(h).padStart(2,"0")}:${String(mm).padStart(2,"0")}`);
                                }}
                              >
                                {timeRows.map((m, i) => <div key={m} className="spa-grid-row-line" style={{ top: i * ROW_H }} />)}
                                {renderApptBlocks(colAppts)}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* ══ AVAILABILITY TAB ═══════════════════════════════ */}
        {tab === "availability" && (
          <div className="spa-avail-section">
            {/* Week navigator */}
            <div className="spa-avail-nav">
              <button className="spa-cal-nav-btn" onClick={() => setWeekStart(d => shiftDate(d, -7))}>
                <ChevronLeft size={16} />
              </button>
              <div className="spa-avail-nav-center">
                <span className="spa-avail-week-label">
                  {fmtShortDate(weekDates[0])} – {fmtShortDate(weekDates[6])}
                </span>
                <button
                  className="spa-btn-ghost spa-avail-today-btn"
                  onClick={() => setWeekStart(getWeekStart(today))}
                >
                  This Week
                </button>
              </div>
              <button className="spa-cal-nav-btn" onClick={() => setWeekStart(d => shiftDate(d, 7))}>
                <ChevronRight size={16} />
              </button>
            </div>

            {/* Legend */}
            <div className="spa-avail-legend">
              {[
                { label: "Available",   color: "#34d399", bg: "rgba(52,211,153,0.15)"  },
                { label: "Busy (45%+)", color: "#fbbf24", bg: "rgba(251,191,36,0.15)"  },
                { label: "Full (85%+)", color: "#f87171", bg: "rgba(239,68,68,0.15)"   },
                { label: "Day Off",     color: "rgba(242,235,217,0.45)", bg: "rgba(30,41,59,0.5)"      },
              ].map(l => (
                <div key={l.label} className="spa-avail-legend-item">
                  <span className="spa-avail-legend-dot" style={{ background: l.color }} />
                  <span style={{ color: "rgba(242,235,217,0.3)", fontSize: 12 }}>{l.label}</span>
                </div>
              ))}
            </div>

            {loadingWeek ? (
              <div className="spa-loading"><Loader2 size={18} className="spa-spinner" /> Loading availability…</div>
            ) : weekAvail.length === 0 ? (
              <div className="spa-empty">
                <div className="spa-empty-icon"><Users size={36} /></div>
                <p>No staff members. Add staff to see availability.</p>
              </div>
            ) : (
              <div className="spa-avail-grid-wrapper">
                <div className="spa-avail-grid" style={{ gridTemplateColumns: `180px repeat(7, 1fr)` }}>
                  {/* Header row */}
                  <div className="spa-avail-header-cell spa-avail-corner">Staff</div>
                  {weekDates.map((date) => {
                    const d = new Date(date + "T12:00:00");
                    const isToday = date === today;
                    return (
                      <div key={date} className={`spa-avail-header-cell ${isToday ? "today" : ""}`}>
                        <div className="spa-avail-dow">{DAY_NAMES[d.getDay()]}</div>
                        <div className="spa-avail-day-num" style={{ color: isToday ? "#818cf8" : "rgba(201,168,76,0.07)" }}>
                          {d.getDate()}
                        </div>
                      </div>
                    );
                  })}

                  {/* Staff rows */}
                  {weekAvail.map(sw => (
                    <>
                      {/* Staff name cell */}
                      <div key={`name-${sw.staff_id}`} className="spa-avail-staff-cell">
                        <div className="spa-avail-staff-avatar">{initials(sw.staff_name)}</div>
                        <div>
                          <div className="spa-avail-staff-name">{sw.staff_name}</div>
                          <div className="spa-avail-staff-role">{sw.role}</div>
                        </div>
                      </div>

                      {/* Day cells */}
                      {sw.days.map(day => (
                        <div
                          key={`${sw.staff_id}-${day.date}`}
                          className={`spa-avail-cell ${day.status} ${drillCell?.staff.staff_id === sw.staff_id && drillCell?.day.date === day.date ? "drill-active" : ""}`}
                          style={{ background: availColor(day.status) }}
                          onClick={() => {
                            if (day.status === "off") return;
                            setDrillCell({ staff: sw, day });
                            const firstId = services.length > 0 ? services[0].id : null;
                            setDrillServiceId(firstId);
                            if (services.length > 0) {
                              fetchDrillSlots(services[0].id, day.date);
                            } else {
                              fetchServices().then(() => {});
                            }
                          }}
                          title={day.status === "off" ? "Day off" : `${day.appointments} appt${day.appointments !== 1 ? "s" : ""} · ${day.utilization}% booked`}
                        >
                          {day.status === "off" ? (
                            <span className="spa-avail-cell-off">Off</span>
                          ) : (
                            <>
                              <div className="spa-avail-cell-util" style={{ color: availTextColor(day.status) }}>
                                {day.utilization}%
                              </div>
                              <div className="spa-avail-cell-count">
                                {day.appointments} appt{day.appointments !== 1 ? "s" : ""}
                              </div>
                              {/* Utilization bar */}
                              <div className="spa-avail-bar-track">
                                <div
                                  className="spa-avail-bar-fill"
                                  style={{
                                    width: `${day.utilization}%`,
                                    background: availTextColor(day.status),
                                  }}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </>
                  ))}
                </div>
              </div>
            )}

            {/* Drill-down panel */}
            {drillCell && (
              <div className="spa-drill-panel">
                <div className="spa-drill-header">
                  <div>
                    <div className="spa-drill-title">
                      {drillCell.staff.staff_name} — {fmtDate(drillCell.day.date)}
                    </div>
                    <div className="spa-drill-meta">
                      {drillCell.day.appointments} appointments · {drillCell.day.utilization}% utilization
                      · {drillCell.day.booked_mins} / {drillCell.day.work_mins} min booked
                    </div>
                  </div>
                  <button className="spa-modal-close" onClick={() => setDrillCell(null)}><X size={16} /></button>
                </div>

                {/* Service selector for slot check */}
                {services.length > 0 && (
                  <div className="spa-drill-svc-row">
                    <span style={{ color: "rgba(242,235,217,0.4)", fontSize: 12 }}>Check slots for:</span>
                    <select
                      className="spa-filter-select"
                      style={{ minWidth: 200 }}
                      value={drillServiceId ?? services[0]?.id}
                      onChange={e => {
                        const id = Number(e.target.value);
                        setDrillServiceId(id);
                        fetchDrillSlots(id, drillCell.day.date);
                      }}
                    >
                      {services.filter(s => s.available).map(s => (
                        <option key={s.id} value={s.id}>{s.name} ({s.duration} min)</option>
                      ))}
                    </select>
                  </div>
                )}

                {drillLoading ? (
                  <div className="spa-loading"><Loader2 size={16} className="spa-spinner" /> Checking slots…</div>
                ) : drillSlots.length === 0 ? (
                  <div style={{ color: "rgba(242,235,217,0.45)", fontSize: 13, padding: "12px 0" }}>No available slots.</div>
                ) : (
                  drillSlots
                    .filter(ss => ss.staff_id === drillCell.staff.staff_id)
                    .map(ss => (
                      <div key={ss.staff_id}>
                        {ss.available_slots.length === 0 ? (
                          <div className="spa-fully-booked" style={{ padding: 12 }}>
                            Fully booked for this service on this day.
                          </div>
                        ) : (
                          <div className="spa-drill-slots">
                            {ss.available_slots.map(slot => (
                              <button
                                key={slot}
                                className="spa-slot-btn"
                                onClick={() => {
                                  const svc = services.find(sv => sv.id === drillServiceId);
                                  openBooking(
                                    drillCell.day.date,
                                    drillCell.staff.staff_id,
                                    drillCell.staff.staff_name,
                                    slot,
                                    svc ? { id: svc.id, name: svc.name, duration: svc.duration, price: svc.price } : undefined,
                                    svc ? 3 : 1,
                                  );
                                  setDrillCell(null);
                                }}
                              >
                                {fmt12(slot)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                )}

                <button
                  className="spa-btn-primary"
                  style={{ marginTop: 16, width: "100%" }}
                  onClick={() => {
                    const svc = services.find(sv => sv.id === drillServiceId);
                    openBooking(
                      drillCell.day.date,
                      drillCell.staff.staff_id,
                      drillCell.staff.staff_name,
                      undefined,
                      svc ? { id: svc.id, name: svc.name, duration: svc.duration, price: svc.price } : undefined,
                      svc ? 2 : 1,
                    );
                    setDrillCell(null);
                  }}
                >
                  <Plus size={14} /> Book {drillCell.staff.staff_name} on {fmtShortDate(drillCell.day.date)}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ══ APPOINTMENTS TAB ═══════════════════════════════ */}
        {tab === "appointments" && (
          <>
            <div className="spa-toolbar">
              <input type="date" className="spa-date-input" value={apptDate} onChange={e => setApptDate(e.target.value)} />
              <select className="spa-filter-select" value={apptStatus} onChange={e => setApptStatus(e.target.value)}>
                <option value="all">All Statuses</option>
                {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <div className="spa-search-box">
                <Search size={14} />
                <input
                  type="text"
                  placeholder="Search guest, service…"
                  value={apptSearch}
                  onChange={e => setApptSearch(e.target.value)}
                />
              </div>
              <span className="spa-toolbar-count">{filteredAppts.length} appointment{filteredAppts.length !== 1 ? "s" : ""}</span>
            </div>

            <div className="spa-list">
              {loadingAppts ? (
                <div className="spa-loading"><Loader2 size={18} className="spa-spinner" /> Loading…</div>
              ) : filteredAppts.length === 0 ? (
                <div className="spa-no-appointments">No appointments found.</div>
              ) : (
                [...filteredAppts].sort((a, b) => `${a.date}${a.time_slot}`.localeCompare(`${b.date}${b.time_slot}`)).map(appt => (
                  <div key={appt.id} className="spa-row">
                    <div className="spa-row-time">
                      <div className="spa-row-time-slot">{fmt12(appt.time_slot)}</div>
                      <div className="spa-row-time-date">{appt.date}</div>
                    </div>
                    <div className="spa-row-guest">
                      <div className="spa-row-guest-name">{appt.guest_name}</div>
                      {appt.room_number && (
                        <div className="spa-row-guest-room">
                          <BedDouble size={10} style={{ display: "inline", marginRight: 3 }} />Room {appt.room_number}
                        </div>
                      )}
                    </div>
                    <div className="spa-row-service">
                      <div className="spa-row-service-name">{appt.service_name}</div>
                      <div className="spa-row-service-dur">{appt.duration} min · ${appt.price}</div>
                    </div>
                    <div className="spa-row-therapist">{appt.staff_name || "—"}</div>
                    <span className="spa-status-badge" style={{ color: STATUS_META[appt.status]?.color, background: STATUS_META[appt.status]?.bg }}>
                      {STATUS_META[appt.status]?.label ?? appt.status}
                    </span>
                    <div className="spa-row-actions">
                      {appt.status === "confirmed" && (
                        <button className="spa-action-btn warn" onClick={() => updateStatus(appt.id, "in_progress")} title="Start"><Activity size={12} /></button>
                      )}
                      {appt.status === "in_progress" && (
                        <button className="spa-action-btn success" onClick={() => updateStatus(appt.id, "completed")} title="Complete"><Check size={12} /></button>
                      )}
                      {(appt.status === "confirmed" || appt.status === "in_progress") && (
                        <button className="spa-action-btn danger" onClick={() => updateStatus(appt.id, "cancelled")} title="Cancel"><XCircle size={12} /></button>
                      )}
                      <button className="spa-action-btn danger" onClick={() => deleteAppt(appt.id)} title="Delete"><Trash2 size={12} /></button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* ══ SERVICES TAB ═══════════════════════════════════ */}
        {tab === "services" && (
          <>
            <div className="spa-toolbar">
              <div className="spa-search-box">
                <Search size={14} />
                <input type="text" placeholder="Search services…" value={svcSearch} onChange={e => setSvcSearch(e.target.value)} />
              </div>
              {isSpaManager && <button className="spa-btn-primary" onClick={openNewSvc}><Plus size={14} /> Add Service</button>}
            </div>

            <div className="spa-services-table">
              <div className="spa-services-header">
                <span>Service</span><span>Category</span><span>Duration</span>
                <span>Price</span><span>Max</span><span>Status</span>
                {isSpaManager && <span>Actions</span>}
              </div>
              {loadingSvc ? (
                <div className="spa-loading"><Loader2 size={18} className="spa-spinner" /> Loading…</div>
              ) : services
                    .filter(s => isSpaManager ? true : s.available)
                    .filter(s => !svcSearch || s.name.toLowerCase().includes(svcSearch.toLowerCase()) || s.category.toLowerCase().includes(svcSearch.toLowerCase()))
                    .length === 0 ? (
                <div className="spa-no-appointments">No services found.</div>
              ) : (
                services
                  .filter(s => isSpaManager ? true : s.available)
                  .filter(s => !svcSearch || s.name.toLowerCase().includes(svcSearch.toLowerCase()) || s.category.toLowerCase().includes(svcSearch.toLowerCase()))
                  .map(svc => (
                    <div key={svc.id} className="spa-service-row">
                      <div>
                        <div className="spa-service-name">{svc.name}</div>
                        {svc.description && <div className="spa-service-desc">{svc.description}</div>}
                      </div>
                      <div><span className={`spa-cat-badge ${catClass(svc.category)}`}>{svc.category}</span></div>
                      <div className="spa-duration-badge"><Clock size={12} /> {svc.duration} min</div>
                      <div className="spa-service-price">${svc.price}</div>
                      <div className="spa-service-parallel">{svc.max_parallel}</div>
                      <div>
                        {isSpaManager ? (
                          <button className={`spa-toggle ${svc.available ? "on" : ""}`} onClick={async () => {
                            await axios.put(`${API}/services/${svc.id}`, { ...svc, available: !svc.available });
                            fetchServices();
                          }}>
                            {svc.available ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
                          </button>
                        ) : (
                          <span className="spa-status-badge" style={{ color: svc.available ? "#34d399" : "#94a3b8", background: svc.available ? "rgba(52,211,153,0.12)" : "rgba(148,163,184,0.1)" }}>
                            {svc.available ? "Active" : "Inactive"}
                          </span>
                        )}
                      </div>
                      {isSpaManager && (
                        <div className="spa-row-actions">
                          <button className="spa-action-btn primary" onClick={() => openEditSvc(svc)}><Pencil size={12} /></button>
                          <button className="spa-action-btn danger" onClick={() => deleteSvc(svc.id)}><Trash2 size={12} /></button>
                        </div>
                      )}
                    </div>
                  ))
              )}
            </div>
          </>
        )}

        {/* ══ STAFF TAB ══════════════════════════════════════ */}
        {tab === "staff" && (
          <>
            {isSpaManager && (
              <div className="spa-toolbar">
                <button className="spa-btn-primary" onClick={openNewStaff}><Plus size={14} /> Add Staff</button>
              </div>
            )}

            {loadingStaff ? (
              <div className="spa-loading"><Loader2 size={18} className="spa-spinner" /> Loading staff…</div>
            ) : staff.filter(s => isSpaManager ? true : s.available).length === 0 ? (
              <div className="spa-empty">
                <div className="spa-empty-icon"><Users size={40} /></div>
                <p>No staff members yet. Add your first therapist.</p>
              </div>
            ) : (
              <div className="spa-staff-grid">
                {staff.filter(s => isSpaManager ? true : s.available).map(s => {
                  const staffServices = s.service_ids?.length > 0 ? services.filter(sv => s.service_ids.includes(sv.id)) : [];
                  return (
                    <div key={s.id} className="spa-staff-card">
                      <div className="spa-staff-card-top">
                        <div className="spa-staff-avatar">{initials(s.name)}</div>
                        <div className="spa-staff-info">
                          <div className="spa-staff-name">{s.name}</div>
                          <div className="spa-staff-role">{s.role}</div>
                        </div>
                        {isSpaManager ? (
                          <button className={`spa-toggle ${s.available ? "on" : ""}`} onClick={async () => {
                            await axios.put(`${API}/staff/${s.id}`, { ...s, available: !s.available });
                            fetchStaff();
                          }}>
                            {s.available ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                          </button>
                        ) : (
                          <span className="spa-status-badge" style={{ color: "#34d399", background: "rgba(52,211,153,0.12)" }}>Active</span>
                        )}
                      </div>
                      <div className="spa-staff-card-meta">
                        <div className="spa-staff-meta-row"><Clock size={12} /> {s.work_start} – {s.work_end}</div>
                      </div>
                      <div className="spa-day-pills">
                        {DAY_NAMES.map((dn, i) => (
                          <span key={dn} className={`spa-day-pill ${s.days_off?.includes(i) ? "off" : ""}`}>{dn}</span>
                        ))}
                      </div>
                      <div className="spa-staff-services">
                        {staffServices.length === 0 ? (
                          <span className="spa-staff-service-badge all-services">All Services</span>
                        ) : (
                          staffServices.slice(0, 4).map(sv => (
                            <span key={sv.id} className="spa-staff-service-badge">{sv.name}</span>
                          ))
                        )}
                        {staffServices.length > 4 && <span className="spa-staff-service-badge">+{staffServices.length - 4} more</span>}
                      </div>
                      {isSpaManager && (
                        <div className="spa-staff-card-actions">
                          <button className="spa-action-btn primary" onClick={() => openEditStaff(s)}><Pencil size={12} /> Edit</button>
                          <button className="spa-action-btn danger" onClick={() => deleteStaff(s.id)}><Trash2 size={12} /> Delete</button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

      {/* ══ ANALYTICS TAB (spa_manager only) ════════════════ */}
      {tab === "analytics" && isSpaManager && (
        <div className="spa-analytics-section">
          {loadingAnalytics ? (
            <div className="spa-loading"><Loader2 size={18} className="spa-spinner" /> Loading analytics…</div>
          ) : !analytics ? (
            <div className="spa-empty"><div className="spa-empty-icon"><TrendingUp size={36} /></div><p>No data yet.</p></div>
          ) : (
            <>
              {/* KPI Row */}
              <div className="spa-kpis">
                <div className="spa-kpi-card">
                  <div className="spa-kpi-icon" style={{ background: "rgba(99,102,241,0.15)" }}><Calendar size={22} color="#818cf8" /></div>
                  <div>
                    <div className="spa-kpi-value">{analytics.alltime_bookings}</div>
                    <div className="spa-kpi-label">All-Time Bookings</div>
                  </div>
                </div>
                <div className="spa-kpi-card">
                  <div className="spa-kpi-icon" style={{ background: "rgba(52,211,153,0.1)" }}><TrendingUp size={22} color="#34d399" /></div>
                  <div>
                    <div className="spa-price-value" style={{ fontSize: 22 }}>${analytics.alltime_revenue.toFixed(0)}</div>
                    <div className="spa-kpi-label">All-Time Revenue</div>
                  </div>
                </div>
                <div className="spa-kpi-card">
                  <div className="spa-kpi-icon" style={{ background: "rgba(251,191,36,0.12)" }}><Activity size={22} color="#fbbf24" /></div>
                  <div>
                    <div className="spa-kpi-value">
                      {analytics.period_bookings}
                      {analytics.bookings_change_pct !== null && (
                        <span className="spa-change-chip" style={{ color: analytics.bookings_change_pct >= 0 ? "#34d399" : "#f87171" }}>
                          {analytics.bookings_change_pct >= 0 ? "+" : ""}{analytics.bookings_change_pct}%
                        </span>
                      )}
                    </div>
                    <div className="spa-kpi-label">Last 30 Days Bookings</div>
                  </div>
                </div>
                <div className="spa-kpi-card">
                  <div className="spa-kpi-icon" style={{ background: "rgba(52,211,153,0.08)" }}><CreditCard size={22} color="#34d399" /></div>
                  <div>
                    <div className="spa-price-value" style={{ fontSize: 22 }}>
                      ${analytics.period_revenue.toFixed(0)}
                      {analytics.revenue_change_pct !== null && (
                        <span className="spa-change-chip" style={{ color: analytics.revenue_change_pct >= 0 ? "#34d399" : "#f87171" }}>
                          {analytics.revenue_change_pct >= 0 ? "+" : ""}{analytics.revenue_change_pct}%
                        </span>
                      )}
                    </div>
                    <div className="spa-kpi-label">Last 30 Days Revenue</div>
                  </div>
                </div>
              </div>

              {/* Revenue Bar Chart */}
              <div className="spa-analytics-card">
                <h3 className="spa-analytics-card-title">Daily Revenue — Last 30 Days</h3>
                <div className="spa-bar-chart">
                  {(() => {
                    const maxRev = Math.max(...analytics.by_day.map(d => d.revenue), 1);
                    return analytics.by_day.map((d, i) => (
                      <div key={d.date} className="spa-bar-col" title={`${d.date}: $${d.revenue.toFixed(0)}, ${d.bookings} booking${d.bookings !== 1 ? "s" : ""}`}>
                        <div className="spa-bar-fill" style={{ height: `${Math.max(2, (d.revenue / maxRev) * 100)}%`, background: d.revenue > 0 ? "#6366f1" : "#1e293b" }} />
                        {i % 7 === 0 && (
                          <div className="spa-bar-label">{new Date(d.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
                        )}
                      </div>
                    ));
                  })()}
                </div>
              </div>

              <div className="spa-analytics-row">
                {/* Top Services */}
                <div className="spa-analytics-card" style={{ flex: 1 }}>
                  <h3 className="spa-analytics-card-title">Top Services (30 days)</h3>
                  {analytics.top_services.length === 0 ? (
                    <div style={{ color: "rgba(242,235,217,0.45)", fontSize: 13 }}>No data yet.</div>
                  ) : (
                    <div className="spa-analytics-table">
                      {analytics.top_services.map((svc, i) => {
                        const maxBookings = analytics.top_services[0]?.bookings || 1;
                        return (
                          <div key={svc.name} className="spa-analytics-row-item">
                            <span className="spa-analytics-rank">{i + 1}</span>
                            <div className="spa-analytics-bar-row">
                              <div className="spa-analytics-name">{svc.name}</div>
                              <div className="spa-analytics-bar-bg">
                                <div className="spa-analytics-bar-fg" style={{ width: `${(svc.bookings / maxBookings) * 100}%` }} />
                              </div>
                            </div>
                            <span className="spa-analytics-val">{svc.bookings} · <span style={{ color: "#34d399" }}>${svc.revenue.toFixed(0)}</span></span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Staff Performance */}
                <div className="spa-analytics-card" style={{ flex: 1 }}>
                  <h3 className="spa-analytics-card-title">Staff Performance (30 days)</h3>
                  {analytics.staff_perf.length === 0 ? (
                    <div style={{ color: "rgba(242,235,217,0.45)", fontSize: 13 }}>No data yet.</div>
                  ) : (
                    <div className="spa-analytics-table">
                      {analytics.staff_perf.map((s, i) => {
                        const maxB = analytics.staff_perf[0]?.bookings || 1;
                        return (
                          <div key={s.name} className="spa-analytics-row-item">
                            <div className="spa-staff-avatar" style={{ width: 28, height: 28, fontSize: 10, flexShrink: 0 }}>{initials(s.name)}</div>
                            <div className="spa-analytics-bar-row">
                              <div className="spa-analytics-name">{s.name}</div>
                              <div className="spa-analytics-bar-bg">
                                <div className="spa-analytics-bar-fg" style={{ width: `${(s.bookings / maxB) * 100}%`, background: "#fbbf24" }} />
                              </div>
                            </div>
                            <span className="spa-analytics-val">{s.bookings} · <span style={{ color: "#34d399" }}>${s.revenue.toFixed(0)}</span></span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="spa-analytics-row">
                {/* Payment Split */}
                <div className="spa-analytics-card" style={{ flex: "0 0 280px" }}>
                  <h3 className="spa-analytics-card-title">Payment Methods (30 days)</h3>
                  {(() => {
                    const total = analytics.payment_split.room_charge + analytics.payment_split.direct_payment || 1;
                    const roomPct  = Math.round((analytics.payment_split.room_charge  / total) * 100);
                    const dirPct   = Math.round((analytics.payment_split.direct_payment / total) * 100);
                    return (
                      <div>
                        <div className="spa-payment-bar" style={{ height: 14, borderRadius: 7, background: "#1e293b", display: "flex", overflow: "hidden", marginBottom: 12 }}>
                          {roomPct > 0 && <div style={{ width: `${roomPct}%`, background: "#818cf8" }} title={`Room charge: ${roomPct}%`} />}
                          {dirPct  > 0 && <div style={{ width: `${dirPct}%`,  background: "#34d399" }} title={`Direct: ${dirPct}%`} />}
                        </div>
                        <div style={{ display: "flex", gap: 16 }}>
                          <div><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#818cf8", marginRight: 6 }} /><span style={{ color: "rgba(242,235,217,0.3)", fontSize: 12 }}>Room Charge</span> <strong style={{ color: "rgba(201,168,76,0.07)", marginLeft: 4 }}>{analytics.payment_split.room_charge}</strong></div>
                          <div><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#34d399", marginRight: 6 }} /><span style={{ color: "rgba(242,235,217,0.3)", fontSize: 12 }}>Direct Pay</span> <strong style={{ color: "rgba(201,168,76,0.07)", marginLeft: 4 }}>{analytics.payment_split.direct_payment}</strong></div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Status distribution */}
                <div className="spa-analytics-card" style={{ flex: "0 0 300px" }}>
                  <h3 className="spa-analytics-card-title">Booking Status (30 days)</h3>
                  {Object.entries(analytics.status_counts).filter(([, v]) => v > 0).map(([k, v]) => {
                    const meta = STATUS_META[k] ?? { label: k, color: "rgba(242,235,217,0.3)", bg: "rgba(148,163,184,0.1)" };
                    const total = Object.values(analytics.status_counts).reduce((s, n) => s + n, 1);
                    return (
                      <div key={k} className="spa-analytics-status-row">
                        <span className="spa-status-badge" style={{ color: meta.color, background: meta.bg, minWidth: 80 }}>{meta.label}</span>
                        <div className="spa-analytics-bar-bg" style={{ flex: 1 }}>
                          <div className="spa-analytics-bar-fg" style={{ width: `${(v / total) * 100}%`, background: meta.color }} />
                        </div>
                        <span style={{ color: "rgba(242,235,217,0.3)", fontSize: 12, minWidth: 28, textAlign: "right" }}>{v}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ══ WAITLIST TAB ════════════════════════════════════ */}
      {tab === "waitlist" && (
        <SpaWaitlist
          API={API}
          services={services}
          onConvertToBooking={(entry) => {
            openBooking(
              entry.preferred_date || undefined,
              undefined,
              undefined,
              undefined,
              entry.service_id ? services.find(s => s.id === entry.service_id) : undefined,
            );
            setBookingData(d => ({ ...d, guest_name: entry.guest_name, phone: entry.phone || "" }));
          }}
        />
      )}

      {/* ══ ROOMS TAB ═══════════════════════════════════════ */}
      {tab === "rooms" && <SpaRooms API={API} isSpaManager={isSpaManager} />}

      {/* ══ GUESTS TAB ══════════════════════════════════════ */}
      {tab === "guests" && <SpaGuests API={API} />}

      {/* ══ PACKAGES TAB ════════════════════════════════════ */}
      {tab === "packages" && isSpaManager && (
        <SpaPackages API={API} services={services} isSpaManager={isSpaManager} />
      )}

      {/* ══ RETAIL TAB ══════════════════════════════════════ */}
      {tab === "retail" && isSpaManager && (
        <SpaRetail API={API} isSpaManager={isSpaManager} />
      )}

      {/* ══ VOUCHERS TAB ════════════════════════════════════ */}
      {tab === "vouchers" && isSpaManager && <SpaVouchers API={API} />}

      {/* ══ SETTLEMENT TAB ══════════════════════════════════ */}
      {tab === "settlement" && isSpaManager && <SpaSettlement API={API} />}

      </div>

      {/* ══════════════════════════════════════════════════════
          APPOINTMENT DETAIL PANEL
      ══════════════════════════════════════════════════════ */}
      {apptDetail && (
        <div className="spa-modal-overlay" onClick={() => setApptDetail(null)}>
          <div className="spa-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="spa-modal-header">
              <div>
                <h2 className="spa-modal-title">{apptDetail.guest_name}</h2>
                <div style={{ fontSize: 12, color: "rgba(242,235,217,0.4)", marginTop: 2 }}>{fmtDate(apptDetail.date)} · {fmt12(apptDetail.time_slot)}</div>
              </div>
              <button className="spa-modal-close" onClick={() => setApptDetail(null)}><X size={16} /></button>
            </div>
            <div className="spa-modal-body">

              {/* Status badge */}
              <div style={{ marginBottom: 18 }}>
                <span className="spa-status-badge" style={{ color: STATUS_META[apptDetail.status]?.color, background: STATUS_META[apptDetail.status]?.bg, fontSize: 12, padding: "4px 12px" }}>
                  {STATUS_META[apptDetail.status]?.label ?? apptDetail.status}
                </span>
                {apptDetail.payment_type === "room_charge" && (
                  <span className="spa-status-badge" style={{ marginLeft: 6, color: "#818cf8", background: "rgba(99,102,241,0.12)", fontSize: 12 }}>
                    <Hotel size={11} style={{ display: "inline", marginRight: 3 }} />Room Charge
                  </span>
                )}
              </div>

              {/* Details grid */}
              <div className="spa-appt-detail-grid">
                {[
                  { icon: <Sparkles size={14} />,  label: "Service",    value: apptDetail.service_name || "—" },
                  { icon: <Users size={14} />,      label: "Therapist",  value: apptDetail.staff_name   || "—" },
                  { icon: <Clock size={14} />,      label: "Duration",   value: `${apptDetail.duration} min` },
                  { icon: <TrendingUp size={14} />, label: "Price",      value: `$${apptDetail.price}` },
                  ...(apptDetail.room_number ? [{ icon: <BedDouble size={14} />, label: "Room", value: `Room ${apptDetail.room_number}` }] : []),
                  ...(apptDetail.phone       ? [{ icon: <Search size={14} />,    label: "Phone", value: apptDetail.phone }] : []),
                  ...(apptDetail.booking_id  ? [{ icon: <CreditCard size={14} />, label: "Booking", value: apptDetail.booking_id }] : []),
                ].map(row => (
                  <div key={row.label} className="spa-appt-detail-row">
                    <span className="spa-appt-detail-icon">{row.icon}</span>
                    <span className="spa-appt-detail-label">{row.label}</span>
                    <span className="spa-appt-detail-value">{row.value}</span>
                  </div>
                ))}
              </div>

              {apptDetail.notes && (
                <div className="spa-appt-detail-notes">
                  <div style={{ fontSize: 11, color: "rgba(242,235,217,0.45)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Notes</div>
                  <div style={{ fontSize: 13, color: "rgba(242,235,217,0.3)" }}>{apptDetail.notes}</div>
                </div>
              )}

              {/* Quick status actions */}
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 11, color: "rgba(242,235,217,0.45)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>Quick Actions</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {apptDetail.status === "confirmed" && (
                    <button className="spa-action-btn" style={{ fontSize: 12, padding: "6px 14px", borderColor: "rgba(251,191,36,0.35)", color: "#fbbf24" }} onClick={async () => {
                      await updateStatus(apptDetail.id, "arrived");
                      setApptDetail(a => a ? { ...a, status: "arrived" } : null);
                    }}><UserCheck size={13} /> Arrived</button>
                  )}
                  {(apptDetail.status === "confirmed" || apptDetail.status === "arrived") && (
                    <button className="spa-action-btn warn" style={{ fontSize: 12, padding: "6px 14px" }} onClick={async () => {
                      await updateStatus(apptDetail.id, "in_progress");
                      setApptDetail(a => a ? { ...a, status: "in_progress" } : null);
                    }}><Activity size={13} /> Start Session</button>
                  )}
                  {apptDetail.status === "in_progress" && (
                    <button className="spa-action-btn success" style={{ fontSize: 12, padding: "6px 14px" }} onClick={async () => {
                      await updateStatus(apptDetail.id, "completed");
                      setApptDetail(a => a ? { ...a, status: "completed" } : null);
                    }}><Check size={13} /> Mark Complete</button>
                  )}
                  {(apptDetail.status === "confirmed" || apptDetail.status === "arrived" || apptDetail.status === "in_progress") && (
                    <button className="spa-action-btn danger" style={{ fontSize: 12, padding: "6px 14px" }} onClick={async () => {
                      await updateStatus(apptDetail.id, "cancelled");
                      setApptDetail(a => a ? { ...a, status: "cancelled" } : null);
                    }}><XCircle size={13} /> Cancel</button>
                  )}
                  {(apptDetail.status === "confirmed" || apptDetail.status === "arrived") && (
                    <button className="spa-action-btn" style={{ fontSize: 12, padding: "6px 14px", borderColor: "rgba(148,163,184,0.2)", color: "rgba(242,235,217,0.3)" }} onClick={async () => {
                      await updateStatus(apptDetail.id, "no_show");
                      setApptDetail(a => a ? { ...a, status: "no_show" } : null);
                    }}><XCircle size={13} /> No Show</button>
                  )}
                  <button className="spa-action-btn danger" style={{ fontSize: 12, padding: "6px 14px" }} onClick={async () => {
                    if (!confirm("Delete this appointment?")) return;
                    await deleteAppt(apptDetail.id);
                    setApptDetail(null);
                  }}><Trash2 size={13} /> Delete</button>
                  <button
                    className="spa-action-btn"
                    style={{ fontSize: 12, padding: "6px 14px", borderColor: "rgba(99,102,241,0.3)", color: "#818cf8" }}
                    onClick={() => setShowIntake(v => !v)}
                  ><ClipboardList size={13} /> {showIntake ? "Hide Intake" : "Intake Form"}</button>
                </div>
              </div>

              {/* Intake form */}
              {showIntake && (
                <div style={{ marginTop: 20, padding: "16px 18px", background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.18)", borderRadius: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#818cf8", marginBottom: 14 }}>Pre-Treatment Intake</div>
                  {intakeData && !showIntake ? null : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <div className="spa-form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: 11 }}>Allergies</label>
                        <input value={intakeForm.allergies} onChange={e => setIntakeForm(f => ({ ...f, allergies: e.target.value }))} placeholder="None known…" />
                      </div>
                      <div className="spa-form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: 11 }}>Medical Conditions</label>
                        <input value={intakeForm.medical_conditions} onChange={e => setIntakeForm(f => ({ ...f, medical_conditions: e.target.value }))} placeholder="None…" />
                      </div>
                      <div className="spa-form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: 11 }}>Pressure Preference</label>
                        <select value={intakeForm.pressure_preference} onChange={e => setIntakeForm(f => ({ ...f, pressure_preference: e.target.value }))}>
                          <option value="light">Light</option>
                          <option value="medium">Medium</option>
                          <option value="firm">Firm</option>
                          <option value="deep">Deep Tissue</option>
                        </select>
                      </div>
                      <div className="spa-form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: 11 }}>Focus Areas</label>
                        <input value={intakeForm.focus_areas} onChange={e => setIntakeForm(f => ({ ...f, focus_areas: e.target.value }))} placeholder="e.g. lower back, shoulders…" />
                      </div>
                      <div className="spa-form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: 11 }}>Additional Notes</label>
                        <textarea rows={2} value={intakeForm.additional_notes} onChange={e => setIntakeForm(f => ({ ...f, additional_notes: e.target.value }))} placeholder="Anything else the therapist should know…" />
                      </div>
                      <button
                        className="spa-btn-primary"
                        style={{ alignSelf: "flex-end", marginTop: 4 }}
                        onClick={saveIntake}
                        disabled={savingIntake}
                      >
                        {savingIntake ? <Loader2 size={13} className="spa-spinner" /> : <Check size={13} />} Save Intake
                      </button>
                    </div>
                  )}
                  {intakeData && !showIntake && (
                    <div style={{ fontSize: 12, color: "rgba(242,235,217,0.5)" }}>Intake saved.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          SERVICE MODAL
      ══════════════════════════════════════════════════════ */}
      {svcModal && editSvc && (
        <div className="spa-modal-overlay" onClick={() => setSvcModal(false)}>
          <div className="spa-modal" onClick={e => e.stopPropagation()}>
            <div className="spa-modal-header">
              <h2 className="spa-modal-title">{editSvc.id ? "Edit Service" : "New Service"}</h2>
              <button className="spa-modal-close" onClick={() => setSvcModal(false)}><X size={16} /></button>
            </div>
            <div className="spa-modal-body">
              <div className="spa-form-group">
                <label>Service Name</label>
                <input type="text" value={editSvc.name || ""} placeholder="e.g. Swedish Massage" onChange={e => setEditSvc(s => ({ ...s, name: e.target.value }))} />
              </div>
              <div className="spa-form-group">
                <label>Category</label>
                <select value={editSvc.category || "Massage"} onChange={e => setEditSvc(s => ({ ...s, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="spa-form-row">
                <div className="spa-form-group" style={{ marginBottom: 0 }}>
                  <label>Duration (minutes)</label>
                  <input type="number" min={15} step={15} value={editSvc.duration ?? 60} onChange={e => setEditSvc(s => ({ ...s, duration: Number(e.target.value) }))} />
                </div>
                <div className="spa-form-group" style={{ marginBottom: 0 }}>
                  <label>Price ($)</label>
                  <input type="number" min={0} step={0.01} value={editSvc.price ?? 0} onChange={e => setEditSvc(s => ({ ...s, price: Number(e.target.value) }))} />
                </div>
              </div>
              <div className="spa-form-group" style={{ marginTop: 16 }}>
                <label>Description</label>
                <textarea value={editSvc.description || ""} placeholder="Brief description…" onChange={e => setEditSvc(s => ({ ...s, description: e.target.value }))} />
              </div>
              <div className="spa-form-row">
                <div className="spa-form-group" style={{ marginBottom: 0 }}>
                  <label>Max Parallel Sessions</label>
                  <input type="number" min={1} value={editSvc.max_parallel ?? 1} onChange={e => setEditSvc(s => ({ ...s, max_parallel: Number(e.target.value) }))} />
                </div>
                <div className="spa-form-group" style={{ marginBottom: 0 }}>
                  <label>Available</label>
                  <label className="spa-checkbox-row">
                    <input type="checkbox" checked={editSvc.available ?? true} onChange={e => setEditSvc(s => ({ ...s, available: e.target.checked }))} />
                    Active & bookable
                  </label>
                </div>
                <div className="spa-form-group" style={{ marginBottom: 0 }}>
                  <label>Requires Staff</label>
                  <label className="spa-checkbox-row">
                    <input type="checkbox" checked={editSvc.requires_staff ?? true} onChange={e => setEditSvc(s => ({ ...s, requires_staff: e.target.checked }))} />
                    Needs a therapist assigned
                  </label>
                </div>
              </div>
            </div>
            <div className="spa-modal-footer">
              <button className="spa-btn-ghost" onClick={() => setSvcModal(false)}>Cancel</button>
              <button className="spa-btn-primary" onClick={saveSvc} disabled={saving}>
                {saving ? <Loader2 size={14} className="spa-spinner" /> : <Check size={14} />}
                {editSvc.id ? "Save Changes" : "Create Service"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          STAFF MODAL
      ══════════════════════════════════════════════════════ */}
      {staffModal && editStaff && (
        <div className="spa-modal-overlay" onClick={() => setStaffModal(false)}>
          <div className="spa-modal wide" onClick={e => e.stopPropagation()}>
            <div className="spa-modal-header">
              <h2 className="spa-modal-title">{editStaff.id ? "Edit Staff Member" : "New Staff Member"}</h2>
              <button className="spa-modal-close" onClick={() => setStaffModal(false)}><X size={16} /></button>
            </div>
            <div className="spa-modal-body">
              <div className="spa-form-row">
                <div className="spa-form-group" style={{ marginBottom: 0 }}>
                  <label>Full Name</label>
                  <input type="text" value={editStaff.name || ""} placeholder="e.g. Maria Chen" onChange={e => setEditStaff(s => ({ ...s, name: e.target.value }))} />
                </div>
                <div className="spa-form-group" style={{ marginBottom: 0 }}>
                  <label>Role / Title</label>
                  <input type="text" value={editStaff.role || ""} placeholder="e.g. Senior Therapist" onChange={e => setEditStaff(s => ({ ...s, role: e.target.value }))} />
                </div>
              </div>
              <div className="spa-form-row" style={{ marginTop: 16 }}>
                <div className="spa-form-group" style={{ marginBottom: 0 }}>
                  <label>Work Start</label>
                  <input type="time" value={editStaff.work_start || "09:00"} onChange={e => setEditStaff(s => ({ ...s, work_start: e.target.value }))} />
                </div>
                <div className="spa-form-group" style={{ marginBottom: 0 }}>
                  <label>Work End</label>
                  <input type="time" value={editStaff.work_end || "18:00"} onChange={e => setEditStaff(s => ({ ...s, work_end: e.target.value }))} />
                </div>
              </div>
              <div className="spa-form-group" style={{ marginTop: 16 }}>
                <label>Days Off</label>
                <div className="spa-days-grid">
                  {DAY_NAMES.map((dn, i) => (
                    <label key={dn} className="spa-day-check">
                      <input
                        type="checkbox"
                        checked={editStaff.days_off?.includes(i) ?? false}
                        onChange={e => {
                          const current = editStaff.days_off || [];
                          setEditStaff(s => ({
                            ...s,
                            days_off: e.target.checked ? [...current, i] : current.filter(d => d !== i),
                          }));
                        }}
                      />
                      <span className="spa-day-check-label">{dn}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="spa-form-group">
                <label>Services (leave all unchecked = can do all)</label>
                <div className="spa-services-checkboxes">
                  {services.map(sv => (
                    <label key={sv.id} className="spa-checkbox-row">
                      <input
                        type="checkbox"
                        checked={editStaff.service_ids?.includes(sv.id) ?? false}
                        onChange={e => {
                          const current = editStaff.service_ids || [];
                          setEditStaff(s => ({
                            ...s,
                            service_ids: e.target.checked ? [...current, sv.id] : current.filter(id => id !== sv.id),
                          }));
                        }}
                      />
                      {sv.name}
                    </label>
                  ))}
                  {services.length === 0 && <span style={{ color: "rgba(242,235,217,0.45)", fontSize: 12 }}>No services defined yet.</span>}
                </div>
              </div>
              <div className="spa-form-group">
                <label>Available</label>
                <label className="spa-checkbox-row">
                  <input type="checkbox" checked={editStaff.available ?? true} onChange={e => setEditStaff(s => ({ ...s, available: e.target.checked }))} />
                  Active & accepting bookings
                </label>
              </div>
            </div>
            <div className="spa-modal-footer">
              <button className="spa-btn-ghost" onClick={() => setStaffModal(false)}>Cancel</button>
              <button className="spa-btn-primary" onClick={saveStaff} disabled={saving}>
                {saving ? <Loader2 size={14} className="spa-spinner" /> : <Check size={14} />}
                {editStaff.id ? "Save Changes" : "Add Staff Member"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          NEW BOOKING WIZARD
      ══════════════════════════════════════════════════════ */}
      {showBooking && (
        <div className="spa-modal-overlay" onClick={() => setShowBooking(false)}>
          <div className="spa-modal wide" onClick={e => e.stopPropagation()}>
            <div className="spa-modal-header">
              <h2 className="spa-modal-title">New Booking</h2>
              <button className="spa-modal-close" onClick={() => setShowBooking(false)}><X size={16} /></button>
            </div>
            <div className="spa-modal-body">

              {/* Step indicator */}
              <div className="spa-step-indicator">
                {[{ n: 1, label: "Select Service" }, { n: 2, label: "Date & Slot" }, { n: 3, label: "Guest Details" }].map((step, idx) => (
                  <div key={step.n} style={{ display: "flex", alignItems: "center", flex: idx < 2 ? 1 : "unset" }}>
                    <div className={`spa-step ${bookingStep === step.n ? "active" : bookingStep > step.n ? "done" : ""}`}>
                      <span className="spa-step-num">{bookingStep > step.n ? <Check size={12} /> : step.n}</span>
                      {step.label}
                    </div>
                    {idx < 2 && <div className="spa-step-line" />}
                  </div>
                ))}
              </div>

              {/* Step 1: Service */}
              {bookingStep === 1 && (
                <div>
                  {loadingSvc ? (
                    <div className="spa-loading"><Loader2 size={18} className="spa-spinner" /> Loading services…</div>
                  ) : (
                    <div className="spa-service-grid">
                      {services.filter(s => s.available).map(svc => (
                        <div
                          key={svc.id}
                          className={`spa-service-card ${bookingData.service_id === svc.id ? "selected" : ""}`}
                          onClick={() => selectService(svc)}
                        >
                          <div style={{ marginBottom: 6 }}>
                            <span className={`spa-cat-badge ${catClass(svc.category)}`}>{svc.category}</span>
                          </div>
                          <div className="spa-service-card-name">{svc.name}</div>
                          {svc.description && (
                            <div style={{ fontSize: 11, color: "rgba(242,235,217,0.4)", marginTop: 4, marginBottom: 4 }}>
                              {svc.description.slice(0, 60)}{svc.description.length > 60 ? "…" : ""}
                            </div>
                          )}
                          <div className="spa-service-card-meta">
                            <span className="spa-service-card-price">${svc.price}</span>
                            <span className="spa-service-card-dur"><Clock size={10} /> {svc.duration} min</span>
                          </div>
                        </div>
                      ))}
                      {services.filter(s => s.available).length === 0 && (
                        <div style={{ color: "rgba(242,235,217,0.45)", fontSize: 13, gridColumn: "1/-1", padding: 16 }}>
                          No available services. Add services in the Services tab.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Date & Slot */}
              {bookingStep === 2 && (
                <div>
                  <div className="spa-form-group">
                    <label>Appointment Date</label>
                    <input
                      type="date"
                      className="spa-date-input"
                      style={{ width: "auto" }}
                      value={bookingData.date || todayStr()}
                      onChange={e => {
                        setBookingData(d => ({ ...d, date: e.target.value, staff_id: undefined, time_slot: undefined }));
                        fetchSlots(bookingData.service_id, e.target.value);
                      }}
                    />
                  </div>

                  {(() => {
                    // Generate 30-min slots 08:00–22:00 for staff-free bookings
                    const noStaffSlots: string[] = [];
                    for (let m = 8 * 60; m + (bookingData.duration ?? 60) <= 22 * 60; m += 30) {
                      const h = Math.floor(m / 60), mm = m % 60;
                      noStaffSlots.push(`${String(h).padStart(2,"0")}:${String(mm).padStart(2,"0")}`);
                    }
                    const noStaff = !slotsLoading && (availableSlots.length === 0 || availableSlots[0]?.no_staff);
                    if (slotsLoading) return (
                      <div className="spa-loading"><Loader2 size={18} className="spa-spinner" /> Checking availability…</div>
                    );
                    if (noStaff) return (
                      <>
                        {availableSlots.length === 0 && (
                          <div style={{ fontSize: 12, color: "rgba(242,235,217,0.4)", marginBottom: 10 }}>
                            No staff assigned — select a time to proceed.
                          </div>
                        )}
                        <div className="spa-slot-grid">
                          {(availableSlots[0]?.available_slots ?? noStaffSlots).map(slot => (
                            <button
                              key={slot}
                              className={`spa-slot-btn ${bookingData.time_slot === slot ? "selected" : ""}`}
                              onClick={() => setBookingData(d => ({ ...d, time_slot: slot, staff_id: undefined, staff_name: undefined }))}
                            >
                              {fmt12(slot)}
                            </button>
                          ))}
                        </div>
                      </>
                    );
                    return (
                    <>
                      <div className="spa-slot-staff-tabs">
                        {availableSlots.map((ss, idx) => (
                          <button
                            key={ss.staff_id}
                            className={`spa-slot-staff-tab ${slotStaffTab === idx ? "active" : ""} ${bookingData.staff_id === ss.staff_id ? "preselected" : ""}`}
                            onClick={() => { setSlotStaffTab(idx); setBookingData(d => ({ ...d, staff_id: undefined, time_slot: undefined })); }}
                          >
                            {ss.staff_name}
                            {ss.available_slots.length === 0 && " (full)"}
                          </button>
                        ))}
                      </div>
                      {availableSlots[slotStaffTab]?.available_slots.length === 0 ? (
                        <div className="spa-fully-booked">Fully booked for this therapist on this date.</div>
                      ) : (
                        <div className="spa-slot-grid">
                          {availableSlots[slotStaffTab].available_slots.map(slot => (
                            <button
                              key={slot}
                              className={`spa-slot-btn ${bookingData.staff_id === availableSlots[slotStaffTab].staff_id && bookingData.time_slot === slot ? "selected" : ""}`}
                              onClick={() => selectSlot(availableSlots[slotStaffTab], slot)}
                            >
                              {fmt12(slot)}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  );
                  })()}
                </div>
              )}

              {/* Step 3: Guest Details + Payment */}
              {bookingStep === 3 && (
                <div>
                  {/* Booking summary */}
                  <div className="spa-booking-summary">
                    {[
                      { label: "Service",   value: bookingData.service_name },
                      { label: "Date",      value: bookingData.date },
                      { label: "Time",      value: bookingData.time_slot ? fmt12(bookingData.time_slot) : "—" },
                      ...(bookingData.staff_name ? [{ label: "Therapist", value: bookingData.staff_name }] : []),
                      { label: "Price",     value: `$${bookingData.price ?? 0}` },
                    ].map(r => (
                      <div key={r.label} className="spa-summary-row">
                        <span className="spa-summary-label">{r.label}</span>
                        <span className="spa-summary-value">{r.value}</span>
                      </div>
                    ))}
                  </div>

                  {/* Payment type toggle */}
                  <div className="spa-form-group">
                    <label>Payment Type</label>
                    <div className="spa-payment-toggle">
                      <button
                        className={`spa-payment-opt ${paymentType === "room_charge" ? "active" : ""}`}
                        onClick={() => {
                          setPaymentType("room_charge");
                          setRoomLookup(null);
                          setRoomSearch("");
                          if (activeBookings.length === 0) fetchActiveBookings();
                        }}
                      >
                        <Hotel size={16} /> Charge to Room
                      </button>
                      <button
                        className={`spa-payment-opt ${paymentType === "direct_payment" ? "active" : ""}`}
                        onClick={() => setPaymentType("direct_payment")}
                      >
                        <Banknote size={16} /> Direct Payment
                      </button>
                    </div>
                  </div>

                  {/* Room charge: active bookings dropdown */}
                  {paymentType === "room_charge" && (
                    <div className="spa-form-group">
                      <label>Select Active Room / Guest</label>

                      {/* Search filter */}
                      <div className="spa-room-lookup-row" style={{ marginBottom: 8 }}>
                        <div style={{ position: "relative", flex: 1 }}>
                          <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "rgba(242,235,217,0.45)" }} />
                          <input
                            type="text"
                            placeholder="Filter by room number or guest name…"
                            value={roomSearch}
                            style={{ paddingLeft: 30 }}
                            onChange={e => setRoomSearch(e.target.value)}
                          />
                        </div>
                        <button
                          className="spa-btn-ghost"
                          style={{ flexShrink: 0 }}
                          disabled={loadingActiveBookings}
                          onClick={fetchActiveBookings}
                          title="Refresh list"
                        >
                          {loadingActiveBookings ? <Loader2 size={13} className="spa-spinner" /> : <RefreshCw size={13} />}
                        </button>
                      </div>

                      {/* List of active bookings */}
                      {loadingActiveBookings ? (
                        <div className="spa-loading" style={{ padding: "10px 0" }}><Loader2 size={14} className="spa-spinner" /> Loading active rooms…</div>
                      ) : activeBookings.length === 0 ? (
                        <div className="spa-room-lookup-result not-found">
                          <XCircle size={14} color="#f87171" />
                          <span style={{ color: "#f87171", fontSize: 12 }}>No active bookings found. Guests may not be checked in yet.</span>
                        </div>
                      ) : (
                        <div className="spa-active-bookings-list">
                          {activeBookings
                            .filter(b => {
                              if (!roomSearch.trim()) return true;
                              const q = roomSearch.toLowerCase();
                              return (
                                (b.room_number || "").toLowerCase().includes(q) ||
                                (b.guest_name  || "").toLowerCase().includes(q)
                              );
                            })
                            .map(b => {
                              const isSelected = roomLookup?.found && roomLookup.booking_id === b.booking_id;
                              return (
                                <div
                                  key={b.booking_id}
                                  className={`spa-active-booking-row ${isSelected ? "selected" : ""}`}
                                  onClick={() => {
                                    setRoomLookup({ found: true, booking_id: b.booking_id, room_id: undefined, guest_name: b.guest_name || undefined, phone: b.phone || undefined, checkin: b.checkin || undefined, checkout: b.checkout || undefined, services_price: b.services_price, total_price: b.total_price, payment_status: b.payment_status || undefined });
                                    setBookingData(d => ({
                                      ...d,
                                      room_number: b.room_number,
                                      guest_name:  d.guest_name || b.guest_name || "",
                                      phone:       d.phone      || b.phone      || "",
                                    }));
                                  }}
                                >
                                  <div className="spa-active-booking-room">
                                    <BedDouble size={14} color="#818cf8" />
                                    <span className="spa-active-booking-room-num">{b.room_number || "—"}</span>
                                  </div>
                                  <div className="spa-active-booking-info">
                                    <div className="spa-active-booking-guest">{b.guest_name || "Unknown Guest"}</div>
                                    <div className="spa-active-booking-meta">
                                      {b.checkin && <span>{b.checkin} → {b.checkout}</span>}
                                      {b.services_price > 0 && <span style={{ color: "#818cf8" }}> · Spa: ${b.services_price}</span>}
                                    </div>
                                  </div>
                                  {isSelected && <CheckCircle2 size={16} color="#34d399" style={{ flexShrink: 0 }} />}
                                </div>
                              );
                            })}
                        </div>
                      )}

                      {/* Confirmed selection */}
                      {roomLookup?.found && (
                        <div className="spa-room-lookup-result found" style={{ marginTop: 8 }}>
                          <CheckCircle2 size={14} color="#34d399" />
                          <div style={{ flex: 1 }}>
                            <div className="spa-lookup-guest">Room {bookingData.room_number} · {roomLookup.guest_name || "Guest"}</div>
                            <div className="spa-lookup-meta">
                              {roomLookup.booking_id}
                              {roomLookup.services_price ? ` · Spa charges so far: $${roomLookup.services_price}` : ""}
                            </div>
                          </div>
                          <button className="spa-btn-ghost" style={{ padding: "2px 8px", fontSize: 11 }} onClick={() => { setRoomLookup(null); }}>
                            <X size={11} /> Clear
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Direct payment: cash / card / instapay */}
                  {paymentType === "direct_payment" && (
                    <div className="spa-form-group">
                      <label>Payment Method</label>
                      <div className="spa-payment-toggle">
                        <button
                          className={`spa-payment-opt ${paymentMethod === "cash" ? "active" : ""}`}
                          onClick={() => setPaymentMethod("cash")}
                        >
                          <Banknote size={15} /> Cash
                        </button>
                        <button
                          className={`spa-payment-opt ${paymentMethod === "card" ? "active" : ""}`}
                          onClick={() => setPaymentMethod("card")}
                        >
                          <CreditCard size={15} /> Card
                        </button>
                        <button
                          className={`spa-payment-opt ${paymentMethod === "instapay" ? "active" : ""}`}
                          onClick={() => setPaymentMethod("instapay")}
                        >
                          <Smartphone size={15} /> Instapay
                        </button>
                      </div>
                      {paymentMethod === "card" && (
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="Last 3 digits on card"
                          value={cardNumber}
                          onChange={e => setCardNumber(e.target.value.replace(/\D/g, ""))}
                          style={{ marginTop: 10 }}
                        />
                      )}
                      {paymentMethod === "instapay" && (
                        <input
                          type="text"
                          placeholder="Transaction reference (optional)"
                          value={transactionRef}
                          onChange={e => setTransactionRef(e.target.value)}
                          style={{ marginTop: 10 }}
                        />
                      )}
                    </div>
                  )}

                  {/* Guest name with autocomplete */}
                  <div className="spa-form-group" style={{ position: "relative" }}>
                    <label>Guest Name *</label>
                    <div style={{ position: "relative" }}>
                      <input
                        type="text"
                        placeholder="Type guest name or room number to search…"
                        value={bookingData.guest_name || ""}
                        autoComplete="off"
                        onChange={e => {
                          const val = e.target.value;
                          setBookingData(d => ({ ...d, guest_name: val }));
                          if (guestSearchTimer.current) clearTimeout(guestSearchTimer.current);
                          if (val.length >= 2) {
                            guestSearchTimer.current = setTimeout(() => searchGuests(val), 400);
                          } else {
                            setGuestSuggestions([]);
                            setShowGuestDropdown(false);
                          }
                        }}
                        onFocus={() => { if (guestSuggestions.length > 0) setShowGuestDropdown(true); }}
                        onBlur={() => setTimeout(() => setShowGuestDropdown(false), 200)}
                      />
                      {searchingGuests && (
                        <Loader2 size={13} className="spa-spinner" style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#818cf8" }} />
                      )}
                      {showGuestDropdown && guestSuggestions.length > 0 && (
                        <div className="spa-guest-dropdown">
                          {guestSuggestions.map((g, i) => (
                            <div
                              key={i}
                              className="spa-guest-dropdown-item"
                              onMouseDown={() => {
                                setBookingData(d => ({
                                  ...d,
                                  guest_name:  g.guest_name || d.guest_name,
                                  phone:       g.phone      || d.phone,
                                  room_number: g.room_number || d.room_number,
                                }));
                                setShowGuestDropdown(false);
                                // If room_charge mode, also trigger room lookup
                                if (paymentType === "room_charge" && g.room_number) {
                                  lookupRoomBooking(g.room_number);
                                }
                              }}
                            >
                              <div className="spa-guest-dropdown-name">{g.guest_name || "Unknown"}</div>
                              <div className="spa-guest-dropdown-meta">
                                {g.room_number && <span><BedDouble size={10} style={{ display: "inline", marginRight: 3 }} />Room {g.room_number}</span>}
                                {g.phone && <span style={{ marginLeft: g.room_number ? 8 : 0 }}>{g.phone}</span>}
                                {g.checkin && <span style={{ marginLeft: 8, color: "rgba(242,235,217,0.45)" }}>{g.checkin} → {g.checkout}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="spa-form-row">
                    {paymentType === "direct_payment" && (
                      <div className="spa-form-group" style={{ marginBottom: 0 }}>
                        <label>Room Number (optional)</label>
                        <input type="text" placeholder="e.g. 204" value={bookingData.room_number || ""} onChange={e => setBookingData(d => ({ ...d, room_number: e.target.value }))} />
                      </div>
                    )}
                    <div className="spa-form-group" style={{ marginBottom: 0 }}>
                      <label>Phone (optional)</label>
                      <input type="tel" placeholder="+1 555 000 0000" value={bookingData.phone || ""} onChange={e => setBookingData(d => ({ ...d, phone: e.target.value }))} />
                    </div>
                  </div>
                  <div className="spa-form-group" style={{ marginTop: 16 }}>
                    <label>Notes (optional)</label>
                    <textarea placeholder="Allergies, preferences, special requests…" value={bookingData.notes || ""} onChange={e => setBookingData(d => ({ ...d, notes: e.target.value }))} />
                  </div>
                </div>
              )}

            </div>
            <div className="spa-modal-footer">
              {bookingStep > 1 && (
                <button className="spa-btn-ghost" onClick={() => setBookingStep(s => (s - 1) as 1 | 2 | 3)}>
                  <ChevronLeft size={14} /> Back
                </button>
              )}
              {bookingStep < 3 && (
                <button
                  className="spa-btn-primary"
                  disabled={(bookingStep === 1 && !bookingData.service_id) || (bookingStep === 2 && (!bookingData.time_slot || (availableSlots.length > 0 && !availableSlots[0]?.no_staff && !bookingData.staff_id)))}
                  onClick={() => { if (bookingStep === 1) goToStep2(); else setBookingStep(3); }}
                >
                  Next <ChevronRight size={14} />
                </button>
              )}
              {bookingStep === 3 && (
                <button
                  className="spa-btn-primary"
                  disabled={
                    saving ||
                    !bookingData.guest_name?.trim() ||
                    (paymentType === "room_charge" && (!roomLookup?.found))
                  }
                  onClick={() => { setSaveError(""); confirmBooking(); }}
                >
                  {saving ? <Loader2 size={14} className="spa-spinner" /> : <CheckCircle2 size={14} />}
                  Confirm Booking
                </button>
              )}
            </div>
            {saveError && (
              <div style={{ color: "#f87171", fontSize: 12, padding: "8px 0", textAlign: "center" }}>
                <FontAwesomeIcon icon={faTriangleExclamation} /> {saveError}
              </div>
            )}
          </div>
        </div>
      )}

    </DashboardLayout>
  );
}
